import React, { useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { makeStyles } from "@material-ui/core";
import { Chart, CommonSeriesSettings, Series, Legend, Tooltip, ValueAxis, Label, ArgumentAxis } from "devextreme-react/chart";
import { controlTypes, externalApiTypes, valueTypeCodes } from "../../../../Config/constants";
import { ArrowRight, ArrowUp, ArrowDown, CheckCircle2 } from "lucide-react";
import Chip from "../../../Shared/ui/Chip";

export default function InsightsTab({ allMeasurements = [], graphProjects = [], currency, loading, timeRangeDates, activeProjection, serverInsights = null }) {
  const { t } = useTranslation();
  const classes = useStyles();
  const currencySymbol = currency?.symbol || "€";

  // ─── Derived data ───────────────────────────────────────────────

  const computedInsights = useMemo(() => {
    if (!allMeasurements || allMeasurements.length === 0) {
      return null;
    }

    const measurements = allMeasurements;

    // Loss = anything not value-added (NVA, Waste, or unclassified non-profit)
    const isLossMeasurement = (m) => !m.isProfit && m.valueTypeId !== valueTypeCodes.VA;

    // Total value created (profit items)
    const totalValueCreated = measurements.filter((m) => m.isProfit && m.eventPrice > 0).reduce((sum, m) => sum + (m.eventPrice || 0), 0);

    // Total production cost (non-profit / cost items)
    const totalProductionLoss = measurements.filter((m) => !m.isProfit && m.eventPrice > 0).reduce((sum, m) => sum + (m.eventPrice || 0), 0);

    // Split production cost into value added vs no value added vs waste
    const valueAddedCost = measurements
      .filter((m) => !m.isProfit && m.eventPrice > 0 && m.valueTypeId === valueTypeCodes.VA)
      .reduce((sum, m) => sum + (m.eventPrice || 0), 0);
    const noValueAddedCost = measurements
      .filter((m) => !m.isProfit && m.eventPrice > 0 && m.valueTypeId === valueTypeCodes.NVA)
      .reduce((sum, m) => sum + (m.eventPrice || 0), 0);
    const wasteCost = measurements
      .filter((m) => !m.isProfit && m.eventPrice > 0 && m.valueTypeId === valueTypeCodes.W)
      .reduce((sum, m) => sum + (m.eventPrice || 0), 0);

    // Actual loss excludes value-added cost
    const totalActualLoss = measurements
      .filter((m) => isLossMeasurement(m) && m.eventPrice > 0)
      .reduce((sum, m) => sum + (m.eventPrice || 0), 0);

    // Netto contribution
    const nettoContribution = totalValueCreated - totalProductionLoss;

    // Date range for per-day calculations — derived from first and last measurement startTime
    const measurementDates = measurements
      .map((m) => (m.startTime ? new Date(m.startTime) : null))
      .filter((d) => d && !isNaN(d));
    const rangeStart = measurementDates.length > 0 ? new Date(Math.min(...measurementDates)) : null;
    const rangeEnd = measurementDates.length > 0 ? new Date(Math.max(...measurementDates)) : null;
    const daySpan = rangeStart && rangeEnd ? Math.max(1, Math.ceil((rangeEnd - rangeStart) / 86400000) + 1) : 1;

    // ─── Loss by line (per registration point / project resource) ───
    const lossByLine = {};
    measurements
      .filter((m) => isLossMeasurement(m) && m.eventPrice > 0)
      .forEach((m) => {
        const lineName = m.resourceName || m.projectName || "Unknown";
        if (!lossByLine[lineName]) lossByLine[lineName] = { value: 0, duration: 0 };
        lossByLine[lineName].value += m.eventPrice || 0;
      });
    // Add duration from all loss stopwatch measurements (regardless of eventPrice)
    measurements
      .filter((m) => isLossMeasurement(m) && (m.typeId === controlTypes.STOPWATCH || m.externalApiTypeId === externalApiTypes.STOPWATCH) && m.durationInMinutes > 0)
      .forEach((m) => {
        const lineName = m.resourceName || m.projectName || "Unknown";
        if (!lossByLine[lineName]) lossByLine[lineName] = { value: 0, duration: 0 };
        lossByLine[lineName].duration += m.durationInMinutes;
      });

    const lossByLineData = Object.entries(lossByLine)
      .map(([name, data]) => ({
        name,
        value: data.value,
        duration: data.duration,
        lossPerWeek: daySpan > 0 ? (data.value / daySpan) * 7 : 0,
        yearlyImpact: daySpan > 0 ? (data.value / daySpan) * 365 : 0,
      }))
      .sort((a, b) => b.value - a.value);

    // ─── Top loss drivers (top 5 descriptions / reasons) ───
    const lossByReason = {};
    measurements
      .filter((m) => isLossMeasurement(m) && m.eventPrice > 0)
      .forEach((m) => {
        const reason = m.description || "Unknown";
        if (!lossByReason[reason]) lossByReason[reason] = { value: 0, duration: 0, valueTypeId: m.valueTypeId };
        lossByReason[reason].value += m.eventPrice || 0;
      });
    // Add duration from all loss stopwatch measurements (regardless of eventPrice)
    measurements
      .filter((m) => isLossMeasurement(m) && (m.typeId === controlTypes.STOPWATCH || m.externalApiTypeId === externalApiTypes.STOPWATCH) && m.durationInMinutes > 0)
      .forEach((m) => {
        const reason = m.description || "Unknown";
        if (!lossByReason[reason]) lossByReason[reason] = { value: 0, duration: 0, valueTypeId: m.valueTypeId };
        lossByReason[reason].duration += m.durationInMinutes;
      });

    const topLossDrivers = Object.entries(lossByReason)
      .map(([reason, data]) => ({
        reason,
        value: data.value,
        duration: data.duration,
        valueTypeId: data.valueTypeId,
        percentage: totalActualLoss > 0 ? ((data.value / totalActualLoss) * 100).toFixed(1) : 0,
      }))
      .sort((a, b) => a.value - b.value)
      .slice(-5);

    // ─── Stopwatch-based stats (disruptions) ───
    const stopwatchMeasurements = measurements.filter(
      (m) => m.typeId === controlTypes.STOPWATCH || m.externalApiTypeId === externalApiTypes.STOPWATCH
    );
    const stopwatchLossMeasurements = stopwatchMeasurements.filter((m) => isLossMeasurement(m));

    // Average loss per day
    const avgLossPerDay = totalActualLoss / daySpan;

    // Average disruptions per day
    const avgDisruptionsPerDay = stopwatchLossMeasurements.length / daySpan;

    // Average disruption duration (minutes)
    const durationsMinutes = stopwatchLossMeasurements
      .map((m) => m.durationInMinutes)
      .filter((d) => d !== null && d !== undefined && d > 0);
    const avgDisruptionDuration =
      durationsMinutes.length > 0 ? durationsMinutes.reduce((s, d) => s + d, 0) / durationsMinutes.length : 0;

    // Percentage of potential value lost (loss vs value created + actual loss)
    const totalPotentialValue = totalValueCreated + totalActualLoss;
    const lossPercentage = totalPotentialValue > 0 ? ((totalActualLoss / totalPotentialValue) * 100).toFixed(1) : 0;

    // Weekly and yearly total loss
    const weeklyTotalLoss = daySpan > 0 ? (totalActualLoss / daySpan) * 7 : 0;
    const yearlyTotalLoss = daySpan > 0 ? (totalActualLoss / daySpan) * 365 : 0;

    // Loss lines vs total lines
    const lossLineCount = Object.keys(lossByLine).length;
    const totalLineCount = graphProjects.length;

    // Worst performing line
    const worstLine = lossByLineData.length > 0 ? lossByLineData[0] : null;
    const secondWorstLine = lossByLineData.length > 1 ? lossByLineData[1] : null;

    return {
      totalValueCreated,
      totalProductionLoss,
      totalActualLoss,
      valueAddedCost,
      noValueAddedCost,
      wasteCost,
      nettoContribution,
      lossPercentage,
      lossByLineData,
      topLossDrivers,
      avgLossPerDay,
      avgDisruptionsPerDay,
      avgDisruptionDuration,
      rangeStart,
      rangeEnd,
      daySpan,
      weeklyTotalLoss,
      yearlyTotalLoss,
      lossLineCount,
      totalLineCount,
      worstLine,
      secondWorstLine,
    };
  }, [allMeasurements, graphProjects]);

  // Prefer the server-computed aggregate (GetGraphInsights) when provided; otherwise fall back to
  // the client-side computation above. The server DTO doesn't carry worstLine/secondWorstLine, so
  // derive them from lossByLineData exactly as the client path does. A null rangeStart means the
  // server found no measurements in range -> treat as "no insights" (same as the client's null).
  const insights = useMemo(() => {
    if (serverInsights) {
      if (!serverInsights.rangeStart) return null;
      const lossByLineData = serverInsights.lossByLineData || [];
      return {
        ...serverInsights,
        worstLine: lossByLineData.length > 0 ? lossByLineData[0] : null,
        secondWorstLine: lossByLineData.length > 1 ? lossByLineData[1] : null,
      };
    }
    return computedInsights;
  }, [serverInsights, computedInsights]);

  // ─── Projected data (only when projection is active) ────────────

  const hasProjection = !!activeProjection && allMeasurements.some((m) => m.projectedEventPrice != null);

  const projectedInsights = useMemo(() => {
    if (!hasProjection || !allMeasurements || allMeasurements.length === 0) return null;

    const measurements = allMeasurements;
    const getPrice = (m) => m.projectedEventPrice ?? m.eventPrice ?? 0;

    // Loss = anything not value-added
    const isLossMeasurement = (m) => !m.isProfit && m.valueTypeId !== valueTypeCodes.VA;

    const totalValueCreated = measurements.filter((m) => m.isProfit && getPrice(m) > 0).reduce((sum, m) => sum + getPrice(m), 0);
    const totalProductionLoss = measurements.filter((m) => !m.isProfit && getPrice(m) > 0).reduce((sum, m) => sum + getPrice(m), 0);
    const valueAddedCost = measurements
      .filter((m) => !m.isProfit && getPrice(m) > 0 && m.valueTypeId === valueTypeCodes.VA)
      .reduce((sum, m) => sum + getPrice(m), 0);
    const noValueAddedCost = measurements
      .filter((m) => !m.isProfit && getPrice(m) > 0 && m.valueTypeId === valueTypeCodes.NVA)
      .reduce((sum, m) => sum + getPrice(m), 0);
    const wasteCost = measurements
      .filter((m) => !m.isProfit && getPrice(m) > 0 && m.valueTypeId === valueTypeCodes.W)
      .reduce((sum, m) => sum + getPrice(m), 0);
    const totalActualLoss = measurements
      .filter((m) => isLossMeasurement(m) && getPrice(m) > 0)
      .reduce((sum, m) => sum + getPrice(m), 0);
    const nettoContribution = totalValueCreated - totalProductionLoss;

    const projMeasurementDates = measurements
      .map((m) => (m.startTime ? new Date(m.startTime) : null))
      .filter((d) => d && !isNaN(d));
    const rangeStart = projMeasurementDates.length > 0 ? new Date(Math.min(...projMeasurementDates)) : null;
    const rangeEnd = projMeasurementDates.length > 0 ? new Date(Math.max(...projMeasurementDates)) : null;
    const daySpan = rangeStart && rangeEnd ? Math.max(1, Math.ceil((rangeEnd - rangeStart) / 86400000) + 1) : 1;

    // Loss by line
    const lossByLine = {};
    measurements
      .filter((m) => isLossMeasurement(m) && getPrice(m) > 0)
      .forEach((m) => {
        const lineName = m.resourceName || m.projectName || "Unknown";
        if (!lossByLine[lineName]) lossByLine[lineName] = { value: 0, duration: 0 };
        lossByLine[lineName].value += getPrice(m);
      });
    measurements
      .filter((m) => isLossMeasurement(m) && (m.typeId === controlTypes.STOPWATCH || m.externalApiTypeId === externalApiTypes.STOPWATCH) && m.durationInMinutes > 0)
      .forEach((m) => {
        const lineName = m.resourceName || m.projectName || "Unknown";
        if (!lossByLine[lineName]) lossByLine[lineName] = { value: 0, duration: 0 };
        lossByLine[lineName].duration += m.durationInMinutes;
      });

    const lossByLineData = Object.entries(lossByLine)
      .map(([name, data]) => ({
        name,
        value: data.value,
        duration: data.duration,
        lossPerWeek: daySpan > 0 ? (data.value / daySpan) * 7 : 0,
        yearlyImpact: daySpan > 0 ? (data.value / daySpan) * 365 : 0,
      }))
      .sort((a, b) => b.value - a.value);

    // Top loss drivers
    const lossByReason = {};
    measurements
      .filter((m) => isLossMeasurement(m) && getPrice(m) > 0)
      .forEach((m) => {
        const reason = m.description || "Unknown";
        if (!lossByReason[reason]) lossByReason[reason] = { value: 0, duration: 0, valueTypeId: m.valueTypeId };
        lossByReason[reason].value += getPrice(m);
      });
    measurements
      .filter((m) => isLossMeasurement(m) && (m.typeId === controlTypes.STOPWATCH || m.externalApiTypeId === externalApiTypes.STOPWATCH) && m.durationInMinutes > 0)
      .forEach((m) => {
        const reason = m.description || "Unknown";
        if (!lossByReason[reason]) lossByReason[reason] = { value: 0, duration: 0, valueTypeId: m.valueTypeId };
        lossByReason[reason].duration += m.durationInMinutes;
      });

    const topLossDrivers = Object.entries(lossByReason)
      .map(([reason, data]) => ({
        reason,
        value: data.value,
        duration: data.duration,
        valueTypeId: data.valueTypeId,
        percentage: totalActualLoss > 0 ? ((data.value / totalActualLoss) * 100).toFixed(1) : 0,
      }))
      .sort((a, b) => a.value - b.value)
      .slice(-5);

    const totalPotentialValue = totalValueCreated + totalActualLoss;
    const lossPercentage = totalPotentialValue > 0 ? ((totalActualLoss / totalPotentialValue) * 100).toFixed(1) : 0;

    const avgLossPerDay = totalActualLoss / daySpan;
    const weeklyTotalLoss = daySpan > 0 ? (totalActualLoss / daySpan) * 7 : 0;
    const yearlyTotalLoss = daySpan > 0 ? (totalActualLoss / daySpan) * 365 : 0;

    const lossLineCount = Object.keys(lossByLine).length;
    const totalLineCount = graphProjects.length;
    const worstLine = lossByLineData.length > 0 ? lossByLineData[0] : null;
    const secondWorstLine = lossByLineData.length > 1 ? lossByLineData[1] : null;

    return {
      totalValueCreated,
      totalProductionLoss,
      totalActualLoss,
      valueAddedCost,
      noValueAddedCost,
      wasteCost,
      nettoContribution,
      lossPercentage,
      lossByLineData,
      topLossDrivers,
      avgLossPerDay,
      daySpan,
      weeklyTotalLoss,
      yearlyTotalLoss,
      lossLineCount,
      totalLineCount,
      worstLine,
      secondWorstLine,
    };
  }, [allMeasurements, graphProjects, hasProjection]);

  // ─── Helpers ────────────────────────────────────────────────────

  const formatCurrency = (value) => {
    return `${currencySymbol} ${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDiff = (actual, projected) => {
    const diff = projected - actual;
    if (Math.abs(diff) < 0.01) return null;
    const sign = diff > 0 ? "+" : "";
    return `${sign}${currencySymbol} ${diff.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatPercentDiff = (actual, projected) => {
    if (actual === 0) return null;
    const diff = ((projected - actual) / Math.abs(actual)) * 100;
    if (Math.abs(diff) < 0.1) return null;
    const sign = diff > 0 ? "+" : "";
    return `${sign}${diff.toFixed(1)}%`;
  };

  const formatDuration = (minutes) => {
    if (minutes < 60) return `${Math.round(minutes)} min`;
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return `${h}h ${m}min`;
  };

  const formatCurrencyShort = (value) => {
    return `${currencySymbol}${Math.round(value).toLocaleString()}`;
  };

  const roundDown = (value) => {
    if (value <= 0) return 0;
    const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
    return Math.floor(value / magnitude) * magnitude;
  };

  const formatCurrencyRounded = (value) => {
    return `${currencySymbol}${roundDown(value).toLocaleString()}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  // ─── Empty state ────────────────────────────────────────────────

  if (loading && !serverInsights) {
    return (
      <div className={classes.loadingContainer}>
        <div className={classes.spinner} />
      </div>
    );
  }

  if (!insights) {
    return (
      <div className={classes.emptyStateContainer}>
        <div className={classes.emptyStateCard}>
          <p className={classes.emptyStateCard__title}>{t('insightsTab.noData')}</p>
          <p className={classes.emptyStateCard__subtitle}>{t('insightsTab.noDataSubtitle')}</p>
        </div>
      </div>
    );
  }

  // ─── Render ─────────────────────────────────────────────────────

  return (
    <div className={classes.insightsContainer}>
        {/* Projection banner */}
        {hasProjection && projectedInsights && (
          <div className={classes.projectionBanner}>
            <div className={classes.projectionBanner__stripeBar} />
            <div className={classes.projectionBanner__body}>
              <span className={classes.projectionBanner__label}>{t('insightsTab.simulationActive')}</span>
              <span className={classes.projectionBanner__desc}>{t('insightsTab.simulationActiveDesc')}</span>
            </div>
          </div>
        )}

        {/* KPI Cards */}
        <div className={classes.kpiRow}>
            <div className={classes.kpiCard}>
            <div className={classes.kpiCard__indicator} style={{ backgroundColor: "#28A745" }} />
            <div className={classes.kpiCard__content}>
                <p className={classes.kpiCard__label}>{t('insightsTab.totalValueCreated')}</p>
                <p className={classes.kpiCard__value}>{formatCurrency(insights.totalValueCreated)}</p>
                {hasProjection && projectedInsights && computedInsights && formatDiff(computedInsights.totalValueCreated, projectedInsights.totalValueCreated) && (
                  <div className={classes.kpiCard__simSection}>
                    <span className={classes.kpiCard__simLabel}>{t('insightsTab.sim')}</span>
                    <span className={classes.kpiCard__simValue}>{formatCurrency(projectedInsights.totalValueCreated)}</span>
                    <span className={projectedInsights.totalValueCreated >= computedInsights.totalValueCreated ? classes.diffPositive : classes.diffNegative}>
                      {formatDiff(computedInsights.totalValueCreated, projectedInsights.totalValueCreated)}
                    </span>
                  </div>
                )}
            </div>
            </div>
            <div className={classes.kpiCard}>
            <div className={classes.kpiCard__indicator} style={{ backgroundColor: "#DC3545" }} />
            <div className={classes.kpiCard__content}>
                <p className={classes.kpiCard__label}>{t('insightsTab.totalProductionCost')}</p>
                <p className={classes.kpiCard__value}>{formatCurrency(insights.totalProductionLoss)}</p>
                {hasProjection && projectedInsights && computedInsights && formatDiff(computedInsights.totalProductionLoss, projectedInsights.totalProductionLoss) && (
                  <div className={classes.kpiCard__simSection}>
                    <span className={classes.kpiCard__simLabel}>{t('insightsTab.sim')}</span>
                    <span className={classes.kpiCard__simValue}>{formatCurrency(projectedInsights.totalProductionLoss)}</span>
                    <span className={projectedInsights.totalProductionLoss <= computedInsights.totalProductionLoss ? classes.diffPositive : classes.diffNegative}>
                      {formatDiff(computedInsights.totalProductionLoss, projectedInsights.totalProductionLoss)}
                    </span>
                  </div>
                )}
            </div>
            {(insights.valueAddedCost > 0 || insights.noValueAddedCost > 0 || insights.wasteCost > 0) && (
              <div className={classes.kpiCard__breakdown}>
                {[
                  { label: t('devExDataTable.valueAdded'), value: insights.valueAddedCost, color: "#28A745" },
                  { label: t('devExDataTable.noValueAdded'), value: insights.noValueAddedCost, color: "#F0AD4E" },
                  { label: t('devExDataTable.waste'), value: insights.wasteCost, color: "#DC3545" },
                ].map((row) => (
                  <div key={row.label} className={classes.kpiCard__breakdownItem}>
                    <span className={classes.kpiCard__breakdownDot} style={{ backgroundColor: row.color }} />
                    <div className={classes.kpiCard__breakdownText}>
                      <p className={classes.kpiCard__breakdownLabel}>{row.label}</p>
                      <p className={classes.kpiCard__breakdownValue}>
                        {formatCurrency(row.value)}
                        {insights.totalProductionLoss > 0 && (
                          <span className={classes.kpiCard__breakdownPct}>
                            {" "}({((row.value / insights.totalProductionLoss) * 100).toFixed(1)}%)
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            </div>
            <div className={classes.kpiCard}>
            <div className={classes.kpiCard__indicator} style={{ backgroundColor: insights.nettoContribution >= 0 ? "#28A745" : "#DC3545" }} />
            <div className={classes.kpiCard__content}>
                <p className={classes.kpiCard__label}>{t('insightsTab.netContribution')}</p>
                <p className={classes.kpiCard__value}>{formatCurrency(insights.nettoContribution)}</p>
                {hasProjection && projectedInsights && computedInsights && formatDiff(computedInsights.nettoContribution, projectedInsights.nettoContribution) && (
                  <div className={classes.kpiCard__simSection}>
                    <span className={classes.kpiCard__simLabel}>{t('insightsTab.sim')}</span>
                    <span className={classes.kpiCard__simValue}>{formatCurrency(projectedInsights.nettoContribution)}</span>
                    <span className={projectedInsights.nettoContribution >= computedInsights.nettoContribution ? classes.diffPositive : classes.diffNegative}>
                      {formatDiff(computedInsights.nettoContribution, projectedInsights.nettoContribution)}
                    </span>
                  </div>
                )}
            </div>
            </div>
        </div>
        {/* KPI summary */}
        {insights.totalActualLoss > 0 ? (
          <div className={classes.kpiSummaryCard}>
              <p className={classes.kpiSummaryCard__text}>
                  <span className={classes.kpiSummaryCard__highlight}>{insights.lossPercentage}%</span> {t('insightsTab.lossPercentageText')}
                  {hasProjection && projectedInsights && computedInsights && projectedInsights.lossPercentage !== computedInsights.lossPercentage && (
                    <span className={classes.projectedInline}>
                      {t('insightsTab.simulationLabel')}: <span className={classes.kpiSummaryCard__highlight}>{projectedInsights.lossPercentage}%</span>
                    </span>
                  )}
              </p>
          </div>
        ) : (
          <div className={classes.noLossCard}>
            <CheckCircle2 size={28} className={classes.noLossCard__icon} />
            <div>
              <p className={classes.noLossCard__title}>{t('insightsTab.noLossTitle')}</p>
              <p className={classes.noLossCard__subtitle}>
                {t('insightsTab.noLossSubtitle')}
              </p>
            </div>
          </div>
        )}

      {insights.totalActualLoss > 0 && (<>

      {/* Charts row */}
      <div className={classes.chartsRow}>
        {/* Loss by line */}
        <div className={classes.chartCard}>
          <p className={classes.chartCard__title}>{t('insightsTab.lossByLine')}</p>
          {insights.lossByLineData.length > 0 ? (
            <table className={classes.lossTable}>
              <thead>
                <tr>
                  <th className={classes.lossTable__th}>{t('insightsTab.line')}</th>
                  <th className={`${classes.lossTable__th} ${classes.lossTable__thRight}`}>{t('insightsTab.timeLost')}</th>
                  <th className={`${classes.lossTable__th} ${classes.lossTable__thRight}`}>{t('insightsTab.lossPerWeek')}</th>
                  {hasProjection && projectedInsights && <th className={`${classes.lossTable__th} ${classes.lossTable__thRight} ${classes.lossTable__thProjected}`}>{t('insightsTab.simulated')}</th>}
                  <th className={`${classes.lossTable__th} ${classes.lossTable__thRight}`}>{t('insightsTab.yearlyImpact')}</th>
                  {hasProjection && projectedInsights && <th className={`${classes.lossTable__th} ${classes.lossTable__thRight} ${classes.lossTable__thProjected}`}>{t('insightsTab.simulated')}</th>}
                </tr>
              </thead>
              <tbody>
                {insights.lossByLineData.map((line, idx) => {
                  const projLine = hasProjection && projectedInsights ? projectedInsights.lossByLineData.find((pl) => pl.name === line.name) : null;
                  return (
                    <tr key={idx} className={classes.lossTable__row}>
                      <td className={classes.lossTable__td}>{line.name}</td>
                      <td className={`${classes.lossTable__td} ${classes.lossTable__tdRight}`}>{formatDuration(line.duration)}</td>
                      <td className={`${classes.lossTable__td} ${classes.lossTable__tdRight}`}>{formatCurrency(line.lossPerWeek)}</td>
                      {hasProjection && projectedInsights && (
                        <td className={`${classes.lossTable__td} ${classes.lossTable__tdRight} ${classes.lossTable__tdProjected}`}>
                          {projLine ? formatCurrency(projLine.lossPerWeek) : "—"}
                          {projLine && formatPercentDiff(line.lossPerWeek, projLine.lossPerWeek) && (
                            <span className={`${classes.lossTable__diff} ${projLine.lossPerWeek <= line.lossPerWeek ? classes.diffPositive : classes.diffNegative}`}>
                              {" "}{formatPercentDiff(line.lossPerWeek, projLine.lossPerWeek)}
                            </span>
                          )}
                        </td>
                      )}
                      <td className={`${classes.lossTable__td} ${classes.lossTable__tdRight}`}>{formatCurrency(line.yearlyImpact)}</td>
                      {hasProjection && projectedInsights && (
                        <td className={`${classes.lossTable__td} ${classes.lossTable__tdRight} ${classes.lossTable__tdProjected}`}>
                          {projLine ? formatCurrency(projLine.yearlyImpact) : "—"}
                          {projLine && formatPercentDiff(line.yearlyImpact, projLine.yearlyImpact) && (
                            <span className={`${classes.lossTable__diff} ${projLine.yearlyImpact <= line.yearlyImpact ? classes.diffPositive : classes.diffNegative}`}>
                              {" "}{formatPercentDiff(line.yearlyImpact, projLine.yearlyImpact)}
                            </span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className={classes.noDataText}>{t('insightsTab.noLossData')}</p>
          )}
        </div>

        {/* Top loss drivers */}
        <div className={classes.chartCard}>
          <p className={classes.chartCard__title}>{t('insightsTab.topLossDrivers')}</p>
          {insights.topLossDrivers.length > 0 ? (
            (() => {
              const chartData = hasProjection && projectedInsights
                ? insights.topLossDrivers.map((d) => {
                    const projDriver = projectedInsights.topLossDrivers.find((pd) => pd.reason === d.reason);
                    return { ...d, projectedValue: projDriver?.value || 0 };
                  })
                : insights.topLossDrivers;

              const handleDrawn = (e) => {
                const svg = e.component.element().querySelector("svg");
                if (!svg || svg.querySelector("#simStripe")) return;
                const ns = "http://www.w3.org/2000/svg";
                let defs = svg.querySelector("defs");
                if (!defs) {
                  defs = document.createElementNS(ns, "defs");
                  svg.insertBefore(defs, svg.firstChild);
                }
                const pattern = document.createElementNS(ns, "pattern");
                pattern.setAttribute("id", "simStripe");
                pattern.setAttribute("patternUnits", "userSpaceOnUse");
                pattern.setAttribute("width", "10");
                pattern.setAttribute("height", "10");
                pattern.setAttribute("patternTransform", "rotate(45)");
                const r1 = document.createElementNS(ns, "rect");
                r1.setAttribute("width", "5"); r1.setAttribute("height", "10"); r1.setAttribute("fill", "#4F5BC7");
                const r2 = document.createElementNS(ns, "rect");
                r2.setAttribute("x", "5"); r2.setAttribute("width", "5"); r2.setAttribute("height", "10"); r2.setAttribute("fill", "#EEF2FF");
                pattern.appendChild(r1); pattern.appendChild(r2);
                defs.appendChild(pattern);
              };

              return (
                <>
                  <Chart
                    id="topLossDriversChart"
                    dataSource={chartData}
                    rotated={true}
                    onDrawn={handleDrawn}
                    customizePoint={(pointInfo) => {
                      if (pointInfo.seriesName === "Simulation") return { color: "url(#simStripe)" };
                      const vt = pointInfo.data?.valueTypeId;
                      if (vt === valueTypeCodes.W) return { color: "#DC3545" };
                      if (vt === valueTypeCodes.NVA) return { color: "#F0AD4E" };
                      return {};
                    }}
                  >
                    <CommonSeriesSettings argumentField="reason" type="bar" barWidth={hasProjection && projectedInsights ? 16 : 32} />
                    <Series valueField="value" name="Actual" color="var(--color-primary)" />
                    {hasProjection && projectedInsights && <Series valueField="projectedValue" name="Simulation" color="#4F5BC7" />}
                    <ArgumentAxis><Label visible={true} /></ArgumentAxis>
                    <ValueAxis><Label format={{ type: "fixedPoint", precision: 0 }} /></ValueAxis>
                    <Legend visible={false} />
                    <Tooltip
                      enabled={true}
                      customizeTooltip={(arg) => {
                        const driver = insights.topLossDrivers.find((d) => d.reason === arg.argument);
                        if (arg.seriesName === "Simulation") {
                          const projDriver = projectedInsights?.topLossDrivers.find((d) => d.reason === arg.argument);
                          return { text: `${arg.argument} (${t('graph.simulation')})\n${formatCurrency(arg.value)} (${projDriver?.percentage || 0}%)` };
                        }
                        return { text: `${arg.argument}\n${formatCurrency(arg.value)} (${driver?.percentage || 0}%)\n${formatDuration(driver?.duration || 0)}` };
                      }}
                    />
                  </Chart>
                  {hasProjection && projectedInsights && (
                    <div className={classes.simLegend}>
                      <div className={classes.simLegend__item}>
                        <div className={classes.simLegend__swatchSolid} />
                        <span className={classes.simLegend__label}>{t('insightsTab.actual')}</span>
                      </div>
                      <div className={classes.simLegend__item}>
                        <div className={classes.simLegend__swatchStripe} />
                        <span className={classes.simLegend__label}>{t('graph.simulation')}</span>
                      </div>
                    </div>
                  )}
                </>
              );
            })()
          ) : (
            <p className={classes.noDataText}>{t('insightsTab.noLossDrivers')}</p>
          )}
        </div>
      </div>

      {/* Key insights */}
      <div className={classes.sentencesCard}>
        <p className={classes.sentencesCard__title}>{t('insightsTab.keyInsights')}</p>
        <div className={classes.sentencesList}>
          {insights.topLossDrivers.length > 0 && (() => {
            const actualLast = insights.topLossDrivers[insights.topLossDrivers.length - 1];
            const projLast = hasProjection && projectedInsights?.topLossDrivers.length > 0
              ? projectedInsights.topLossDrivers[projectedInsights.topLossDrivers.length - 1]
              : null;
            const isImprovement = projLast && parseFloat(projLast.percentage) <= parseFloat(actualLast.percentage);
            return (
              <div className={classes.sentence}>
                <span className={classes.sentence__highlight}>{actualLast.reason}</span> {t('insightsTab.accountsFor')}{" "}
                <span className={classes.sentence__highlight}>{actualLast.percentage}%</span> {t('insightsTab.ofTotalProductionLoss')}
                {projLast && (
                  <span className={classes.simChipPair}>
                    <Chip variant="simulation" size="small">{projLast.percentage}%</Chip>
                    <Chip variant={isImprovement ? "success" : "danger"} size="small">
                      {isImprovement ? <ArrowDown size={12} /> : <ArrowUp size={12} />}
                    </Chip>
                  </span>
                )}
              </div>
            );
          })()}
          <div className={classes.sentence}>
            {t('insightsTab.avgProductionLossIs')} <span className={classes.sentence__highlight}>{formatCurrency(insights.avgLossPerDay)}</span> {t('insightsTab.perDay')}
            {hasProjection && projectedInsights && formatDiff(insights.avgLossPerDay, projectedInsights.avgLossPerDay) && (() => {
              const isImprovement = projectedInsights.avgLossPerDay <= insights.avgLossPerDay;
              return (
                <span className={classes.simChipPair}>
                  <Chip variant="simulation" size="small">{formatCurrency(projectedInsights.avgLossPerDay)}</Chip>
                  <Chip variant={isImprovement ? "success" : "danger"} size="small">
                    {isImprovement ? <ArrowDown size={12} /> : <ArrowUp size={12} />}
                  </Chip>
                </span>
              );
            })()}
          </div>
          <div className={classes.sentence}>
            {t('insightsTab.weeklyProductionLossAmountsTo')} <span className={classes.sentence__highlight}>{formatCurrency(insights.weeklyTotalLoss)}</span>
            {hasProjection && projectedInsights && formatDiff(insights.weeklyTotalLoss, projectedInsights.weeklyTotalLoss) && (() => {
              const isImprovement = projectedInsights.weeklyTotalLoss <= insights.weeklyTotalLoss;
              return (
                <span className={classes.simChipPair}>
                  <Chip variant="simulation" size="small">{formatCurrency(projectedInsights.weeklyTotalLoss)}</Chip>
                  <Chip variant={isImprovement ? "success" : "danger"} size="small">
                    {isImprovement ? <ArrowDown size={12} /> : <ArrowUp size={12} />}
                  </Chip>
                </span>
              );
            })()}
          </div>
          <div className={classes.sentence}>
            {t('insightsTab.annualImpactExceeds')} <span className={classes.sentence__highlight}>{formatCurrencyRounded(insights.yearlyTotalLoss)}</span>
            {hasProjection && projectedInsights && formatDiff(insights.yearlyTotalLoss, projectedInsights.yearlyTotalLoss) && (() => {
              const isImprovement = projectedInsights.yearlyTotalLoss <= insights.yearlyTotalLoss;
              return (
                <span className={classes.simChipPair}>
                  <Chip variant="simulation" size="small">{formatCurrencyRounded(projectedInsights.yearlyTotalLoss)}</Chip>
                  <Chip variant={isImprovement ? "success" : "danger"} size="small">
                    {isImprovement ? <ArrowDown size={12} /> : <ArrowUp size={12} />}
                  </Chip>
                </span>
              );
            })()}
          </div>
          <div className={classes.sentence}>
            {t('insightsTab.lossSpreadAcross')} <span className={classes.sentence__highlight}>{insights.lossLineCount} {t('insightsTab.outOf')} {insights.totalLineCount}</span> {t('insightsTab.productionLines')}
          </div>
          {insights.worstLine && (
            <div className={classes.sentence}>
              <span className={classes.sentence__highlight}>{insights.worstLine.name}</span> {t('insightsTab.worstPerformingLine')} (<span className={classes.sentence__highlight}>{formatCurrency(insights.worstLine.lossPerWeek)}</span> {t('insightsTab.lossPerWeekSuffix')})
              {hasProjection && projectedInsights?.worstLine && (() => {
                const isImprovement = projectedInsights.worstLine.lossPerWeek <= insights.worstLine.lossPerWeek;
                return (
                  <span className={classes.simChipPair}>
                    <Chip variant="simulation" size="small">{formatCurrency(projectedInsights.worstLine.lossPerWeek)}/{t('insightsTab.week')}</Chip>
                    <Chip variant={isImprovement ? "success" : "danger"} size="small">
                      {isImprovement ? <ArrowDown size={12} /> : <ArrowUp size={12} />}
                    </Chip>
                  </span>
                );
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Improvement potential */}
      {insights.worstLine && (
        <div className={classes.sentencesCard}>
          <p className={classes.sentencesCard__title}>{t('insightsTab.improvementPotential')}</p>
          <div className={classes.sentencesList}>
            <div className={classes.improvementBlock}>
              <p className={classes.sentence}>
                {t('insightsTab.reducingLossesOn')} <span className={classes.sentence__highlight}>{insights.worstLine.name}</span> {t('insightsTab.byTenPercent')}:
              </p>
              <p className={classes.improvementArrow}><ArrowRight size={14} className={classes.improvementArrowIcon} /> <span className={classes.sentence__highlight}>{formatCurrencyShort(insights.worstLine.lossPerWeek * 0.1)}</span> {t('insightsTab.savingsPerWeek')}</p>
              <p className={classes.improvementArrow}><ArrowRight size={14} className={classes.improvementArrowIcon} /> <span className={classes.sentence__highlight}>{formatCurrencyShort(insights.worstLine.yearlyImpact * 0.1)}</span> {t('insightsTab.perYear')}</p>
            </div>
            {insights.secondWorstLine && (
              <div className={classes.improvementBlock}>
                <p className={classes.sentence}>
                  {t('insightsTab.reducingTop2Lines')}:
                </p>
                <p className={classes.improvementArrow}><ArrowRight size={14} className={classes.improvementArrowIcon} /> <span className={classes.sentence__highlight}>{formatCurrencyRounded((insights.worstLine.yearlyImpact + insights.secondWorstLine.yearlyImpact) * 0.1)}+</span> {t('insightsTab.annualImprovementPotential')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recommended starting point */}
      {insights.worstLine && (
        <div className={classes.sentencesCard}>
          <p className={classes.sentencesCard__title}>{t('insightsTab.recommendedStartingPoint')}</p>
          <div className={classes.sentencesList}>
            <div className={classes.improvementBlock}>
              <p className={classes.sentence}>
                {t('insightsTab.focusOn')} <span className={classes.sentence__highlight}>{insights.worstLine.name}</span>{insights.secondWorstLine ? <> {t('insightsTab.and')} <span className={classes.sentence__highlight}>{insights.secondWorstLine.name}</span></> : ""}:
              </p>
              <p className={classes.improvementArrow}><ArrowRight size={14} className={classes.improvementArrowIcon} /> {t('insightsTab.highestFinancialImpact')}</p>
              <p className={classes.improvementArrow}><ArrowRight size={14} className={classes.improvementArrowIcon} /> {t('insightsTab.fastestROI')}</p>
              <p className={classes.improvementArrow}><ArrowRight size={14} className={classes.improvementArrowIcon} /> {t('insightsTab.immediateVisibility')}</p>
            </div>
          </div>
        </div>
      )}
      </>)}

      {/* Analysis disclaimer */}
      <div className={classes.disclaimerCard}>
        <p className={classes.disclaimerCard__title}>{t('insightsTab.disclaimerTitle')}</p>
        <div className={classes.disclaimerList}>
          <p className={classes.disclaimerItem}>{t('insightsTab.timeframe')}: {formatDate(insights.rangeStart)} – {formatDate(insights.rangeEnd)} ({insights.daySpan} {insights.daySpan === 1 ? t('insightsTab.day') : t('insightsTab.days')})</p>
          <p className={classes.disclaimerItem}>{t('insightsTab.scope')}: {insights.totalLineCount} {insights.totalLineCount !== 1 ? t('insightsTab.productionLines') : t('insightsTab.productionLine')}</p>
          <p className={classes.disclaimerItem}>{t('insightsTab.disclaimerNote')}</p>
        </div>
      </div>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  insightsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "0px 0px 16px 0",
    paddingBottom: "0px",
    width: "100%",
  },

  // ─── KPI cards ───
  kpiRow: {
    display: "flex",
    gap: "16px",
    flexWrap: "wrap",

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },

  kpiCard: {
    flex: 1,
    minWidth: "200px",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #EDEDED",
    display: "flex",
    flexDirection: "row",
    overflow: "hidden",
  },

  kpiCard__indicator: {
    width: "4px",
    flexShrink: 0,
  },

  kpiCard__content: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    padding: "20px 24px",
  },

  kpiCard__wide: {
    flex: 1,
  },

  kpiCard__breakdown: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "8px",
    padding: "12px 16px",
    marginLeft: "auto",
    borderLeft: "1px solid #EDEDED",
    minWidth: 0,
  },

  kpiCard__breakdownItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  kpiCard__breakdownDot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    flexShrink: 0,
  },

  kpiCard__breakdownText: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
  },

  kpiCard__breakdownLabel: {
    margin: 0,
    fontSize: "10px",
    fontFamily: "Lato, sans-serif",
    color: "#6C6C6C",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },

  kpiCard__breakdownValue: {
    margin: 0,
    fontSize: "13px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 700,
    color: "#0C0E29",
  },

  kpiCard__breakdownPct: {
    fontSize: "11px",
    fontWeight: 400,
    color: "#6C6C6C",
  },

  kpiCard__label: {
    margin: 0,
    fontSize: "13px",
    fontFamily: "Lato, sans-serif",
    color: "#6C6C6C",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },

  kpiCard__value: {
    margin: 0,
    fontSize: "28px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 700,
    color: "#0C0E29",
  },

  kpiSummaryCard: {
    padding: "12px",
    borderRadius: "8px",
    backgroundColor: "#FFF9ED",
    border: "1px solid #FEF1D6",
  },

  kpiSummaryCard__text: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    margin: 0,
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
  },

  kpiSummaryCard__highlight: {
    fontWeight: 700,
    fontSize: "18px",
  },

  noLossCard: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "20px 24px",
    borderRadius: "8px",
    backgroundColor: "#F1FBF4",
    border: "1px solid #C8EAD3",
  },

  noLossCard__icon: {
    color: "#28A745",
    flexShrink: 0,
  },

  noLossCard__title: {
    margin: 0,
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 700,
    color: "#0C0E29",
  },

  noLossCard__subtitle: {
    margin: "4px 0 0 0",
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    color: "#3A6F4A",
    lineHeight: "1.5",
  },

  // ─── Charts ───
  chartsRow: {
    display: "flex",
    gap: "16px",
    flexWrap: "wrap",

    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },

  chartCard: {
    flex: 1,
    minWidth: "300px",
    padding: "20px 24px",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #EDEDED",
  },

  chartCard__title: {
    margin: "0 0 12px 0",
    fontSize: "18px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 'normal',
    color: "#0C0E29",
  },

  lossTable: {
    width: "100%",
    borderCollapse: "collapse",
  },

  lossTable__th: {
    textAlign: "left",
    padding: "8px 12px",
    fontSize: "12px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 600,
    color: "#6C6C6C",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: "2px solid #EDEDED",
  },

  lossTable__thRight: {
    textAlign: "right",
  },

  lossTable__row: {
    "&:hover": {
      backgroundColor: "#F9F9F9",
    },
  },

  lossTable__td: {
    padding: "10px 12px",
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
    borderBottom: "1px solid #F0F0F0",
  },

  lossTable__tdRight: {
    textAlign: "right",
    fontWeight: 600,
  },

  noDataText: {
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    color: "#6C6C6C",
    textAlign: "center",
    padding: "24px 0",
  },

  // ─── Sentences ───
  sentencesCard: {
    padding: "20px 24px",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    border: "1px solid #EDEDED",
  },

  sentencesCard__title: {
    margin: "0 0 16px 0",
    fontSize: "18px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 'normal',
    color: "#0C0E29",
  },

  sentencesList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  sentence: {
    margin: 0,
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
    lineHeight: "1.6",
    padding: "8px 12px",
    borderRadius: "6px",
    backgroundColor: "#F9F9F9",
  },

  sentence__highlight: {
    fontWeight: 700,
    color: "var(--color-primary)",
  },

  // ─── States ───
  loadingContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "300px",
  },

  spinner: {
    width: "32px",
    height: "32px",
    border: "3px solid rgba(12, 14, 41, 0.1)",
    borderTop: "3px solid rgba(12, 14, 41, 0.7)",
    borderRadius: "50%",
    animation: "$spin 1s linear infinite",
  },

  "@keyframes spin": {
    "0%": { transform: "rotate(0deg)" },
    "100%": { transform: "rotate(360deg)" },
  },

  emptyStateContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "300px",
  },

  emptyStateCard: {
    padding: "24px",
    textAlign: "center",
  },

  emptyStateCard__title: {
    margin: "0 0 8px 0",
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 600,
    color: "#0C0E29",
  },

  emptyStateCard__subtitle: {
    margin: 0,
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    color: "#6C6C6C",
  },

  // ─── Improvement & recommendation ───
  improvementBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  improvementArrow: {
    margin: 0,
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    color: "#0C0E29",
    lineHeight: "1.6",
    paddingLeft: "24px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },

  improvementArrowIcon: {
    flexShrink: 0,
    color: "var(--color-primary)",
  },

  // ─── Disclaimer ───
  disclaimerCard: {
    padding: "16px 24px",
    borderRadius: "8px",
    backgroundColor: "#F5F5F5",
    border: "1px solid #EDEDED",
  },

  disclaimerCard__title: {
    margin: "0 0 12px 0",
    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 600,
    color: "#6C6C6C",
  },

  disclaimerList: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  disclaimerItem: {
    margin: 0,
    fontSize: "13px",
    fontFamily: "Lato, sans-serif",
    color: "#6C6C6C",
    lineHeight: "1.5",
  },

  // ─── Simulation chip pair (Key insights) ───
  simChipPair: {
    display: "inline-flex",
    alignItems: "center",
    gap: "3px",
    marginLeft: "8px",
    verticalAlign: "middle",
  },

  // ─── Projection banner ───
  projectionBanner: {
    display: "flex",
    borderRadius: "8px",
    border: "1px solid #C7D2FE",
    overflow: "hidden",
    backgroundColor: "#FAFBFF",
  },

  projectionBanner__stripeBar: {
    width: "4px",
    flexShrink: 0,
    background: "repeating-linear-gradient(45deg, #4F5BC7 0px, #4F5BC7 5px, #6366F1 5px, #6366F1 10px)",
  },

  projectionBanner__body: {
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    gap: "14px",
  },

  projectionBanner__label: {
    fontSize: "12px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 700,
    color: "#3730A3",
    textTransform: "uppercase",
    letterSpacing: "0.8px",
    whiteSpace: "nowrap",
  },

  projectionBanner__desc: {
    fontSize: "13px",
    fontFamily: "Lato, sans-serif",
    color: "#6C6C6C",
  },

  // ─── KPI simulation row ───
  kpiCard__simSection: {
    display: "flex",
    alignItems: "baseline",
    gap: "8px",
    paddingTop: "10px",
    marginTop: "4px",
    borderTop: "1.5px dashed var(--color-border-simulation)",
    flexWrap: "wrap",
  },

  kpiCard__simLabel: {
    fontSize: "10px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 700,
    color: "var(--color-text-simulation)",
    textTransform: "uppercase",
    letterSpacing: "0.8px",
    alignSelf: "center",
    flexShrink: 0,
  },

  kpiCard__simValue: {
    fontSize: "18px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 700,
    color: "var(--color-text-simulation)",
  },

  diffPositive: {
    color: "#28A745",
    fontWeight: 600,
    fontSize: "12px",
  },

  diffNegative: {
    color: "#DC3545",
    fontWeight: 600,
    fontSize: "12px",
  },

  // ─── Inline simulation tag (kpiSummaryCard) ───
  projectedInline: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    marginLeft: "10px",
    padding: "2px 8px",
    borderRadius: "4px",
    background: "repeating-linear-gradient(45deg, rgba(79,91,199,0.08) 0, rgba(79,91,199,0.08) 3px, transparent 3px, transparent 9px), #EEF2FF",
    border: "1px solid #C7D2FE",
    color: "var(--color-text-simulation)",
    fontSize: "15px",
    fontFamily: "Lato, sans-serif",
    fontWeight: 500,
    verticalAlign: "middle",
  },

  projectedArrowIcon: {
    verticalAlign: "middle",
    color: "var(--color-text-simulation)",
    flexShrink: 0,
  },

  // ─── Simulation legend (Top loss drivers chart) ───
  simLegend: {
    display: "flex",
    gap: "16px",
    paddingTop: "10px",
    borderTop: "1px solid #EDEDED",
    marginTop: "4px",
    justifyContent: "flex-end",
  },

  simLegend__item: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },

  simLegend__swatchSolid: {
    width: "24px",
    height: "12px",
    borderRadius: "2px",
    backgroundColor: "var(--color-primary)",
    flexShrink: 0,
  },

  simLegend__swatchStripe: {
    width: "24px",
    height: "12px",
    borderRadius: "2px",
    background: "repeating-linear-gradient(45deg, #4F5BC7 0px, #4F5BC7 4px, #6366F1 4px, #6366F1 8px)",
    flexShrink: 0,
  },

  simLegend__label: {
    fontSize: "12px",
    fontFamily: "Lato, sans-serif",
    color: "#6C6C6C",
  },

  // ─── Table simulation columns ───
  lossTable__thProjected: {
    background: "var(--color-bg-simulation)",
    color: "var(--color-text-simulation)",
    fontWeight: 700,
    letterSpacing: "0.3px",
  },

  lossTable__tdProjected: {
    color: "var(--color-text-simulation)",
    fontWeight: 600,
  },

  lossTable__diff: {
    fontSize: "11px",
    fontWeight: 600,
    marginLeft: "4px",
  },
}));
